import { useTracking } from '@change-corgi/core/react/tracking';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useFlyerDownloadModalContext } from 'src/app/pages/starterDashboardV2/shared/components';
import { useSharingModal } from 'src/app/pages/starterDashboardV2/shared/contexts';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';

import {
	type UseSharingToolsReturn as BaseUseSharingToolsReturn,
	useSharingTools as useBaseSharingTools,
} from './index';

type SharingToolsActions = {
	navigateToAllTools: () => void;
	navigateToGroupRecommendations: () => void;
	navigateToReadyToSharePosts: () => void;
	navigateToShareVisual: () => void;
	openFlyerDownloadModal: () => void;
	navigateToEditPetition: () => void;
	openRTSFacebookSharingModal: () => void;
	openRTSInstagramSharingModal: () => void;
};

type UseSharingToolsReturn = Omit<BaseUseSharingToolsReturn, 'actions'> & { actions: SharingToolsActions };

const RTS_TRACKING_PROPS = {
	context: 'petition_dashboard_grow_petition_v2',
	viewContext: 'home',
} as const;

const CLICK_EVENT_NAME = 'petition_manage_ready_to_share_posts_click';

function trackRTSFacebookClick(track: ReturnType<typeof useTracking>) {
	void track(CLICK_EVENT_NAME, {
		context: 'petition_dashboard_grow_petition_v2',
		view_context: 'home',
		location: 'facebook_post',
	});
}

function trackRTSInstagramClick(track: ReturnType<typeof useTracking>) {
	void track(CLICK_EVENT_NAME, {
		context: 'petition_dashboard_grow_petition_v2',
		view_context: 'home',
		location: 'instagram_story',
	});
}

export function useSharingTools(): UseSharingToolsReturn {
	const { data, actions } = useBaseSharingTools('petition_dashboard_grow_petition_v2');
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const { openModal: openFlyerDownloadModal } = useFlyerDownloadModalContext();
	const { startSharingFlow } = useSharingModal();
	const isMobile = useIsMobile();
	const track = useTracking();
	return {
		data,
		actions: {
			...actions,
			openFlyerDownloadModal: () => {
				void track('petition_manage_grow_your_petition_click', {
					context: 'petition_dashboard_grow_petition_v2',
					content: 'poster',
					cta: 'button',
				});
				openFlyerDownloadModal('starter_dashboard_sharing_tools');
			},
			openRTSFacebookSharingModal: () => {
				trackRTSFacebookClick(track);
				startSharingFlow({
					platform: 'facebook',
					postType: 'post',
					petition,
					defaultOpen: false,
					trackingProps: RTS_TRACKING_PROPS,
				});
			},
			openRTSInstagramSharingModal: () => {
				trackRTSInstagramClick(track);
				if (isMobile) {
					startSharingFlow({
						platform: 'instagram',
						postType: 'story',
						petition,
						defaultOpen: false,
						trackingProps: RTS_TRACKING_PROPS,
					});
				} else {
					actions.navigateToReadyToSharePosts();
				}
			},
		},
	};
}
